import React, { useState, useMemo, useEffect } from 'react';
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Collapse from "@mui/material/Collapse";
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import {Build, BuildTrackChoices, BuildPlatformChoices} from "../../../models/build";

interface BuildSelectorProps {
    builds: Build[];
    value: string;
    onChange: (buildId: string) => void;
    label?: string;
    required?: boolean;
    error?: boolean;
    helperText?: string;
    disabled?: boolean;
    fullWidth?: boolean;
}

export default function BuildSelector({
    builds,
    value,
    onChange,
    label = "Build",
    required = false,
    error = false,
    helperText,
    disabled = false,
    fullWidth = true
}: BuildSelectorProps) {
    const [filterTrack, setFilterTrack] = useState<string>('');
    const [filterPlatform, setFilterPlatform] = useState<string>('');
    const [filterVersion, setFilterVersion] = useState<string>('');
    const [showFilters, setShowFilters] = useState(false);

    const formatBuildLabel = (build: Build): string => {
        const parts: string[] = [];
        
        if (build.version_number) {
            parts.push(`v${build.version_number}`);
        }
        
        if (build.platform) {
            const platformLabels: { [key: string]: string } = {
                'macos': 'macOS',
                'windows': 'Windows',
                'linux': 'Linux',
                'web': 'Web',
                'android': 'Android',
                'ios': 'iOS',
                'server': 'Server'
            };
            parts.push(platformLabels[build.platform] || build.platform);
        }
        
        if (build.track) {
            const trackLabels: { [key: string]: string } = {
                'stable': 'Stable',
                'beta': 'Beta',
                'dev': 'Dev'
            };
            parts.push(trackLabels[build.track] || build.track);
        }
        
        return parts.length > 0 ? parts.join(' - ') : build.id;
    };

    const formatDate = (dateString?: string): string => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch {
            return dateString;
        }
    };

    // Get builds filtered by other filters (excluding the current filter being computed)
    const buildsForTrackFilter = useMemo(() => {
        return builds.filter(build => {
            if (filterPlatform && build.platform !== filterPlatform) return false;
            return !(filterVersion && build.version_number !== filterVersion);

        });
    }, [builds, filterPlatform, filterVersion]);

    const buildsForPlatformFilter = useMemo(() => {
        return builds.filter(build => {
            if (filterTrack && build.track !== filterTrack) return false;
            return !(filterVersion && build.version_number !== filterVersion);

        });
    }, [builds, filterTrack, filterVersion]);

    const buildsForVersionFilter = useMemo(() => {
        return builds.filter(build => {
            if (filterTrack && build.track !== filterTrack) return false;
            return !(filterPlatform && build.platform !== filterPlatform);

        });
    }, [builds, filterTrack, filterPlatform]);

    // Get unique values for filter dropdowns based on other filter selections
    const uniqueTracks = useMemo(() => {
        const tracks = buildsForTrackFilter.map(b => b.track).filter(Boolean);
        return Array.from(new Set(tracks));
    }, [buildsForTrackFilter]);

    const uniquePlatforms = useMemo(() => {
        const platforms = buildsForPlatformFilter.map(b => b.platform).filter(Boolean);
        return Array.from(new Set(platforms));
    }, [buildsForPlatformFilter]);

    const uniqueVersions = useMemo(() => {
        const versions = buildsForVersionFilter.map(b => b.version_number).filter(Boolean);
        // Sort versions in descending order (newest first)
        return Array.from(new Set(versions)).sort((a, b) => {
            // Simple version comparison - split by dots and compare numerically
            const aParts = a.split('.').map(Number);
            const bParts = b.split('.').map(Number);
            const maxLength = Math.max(aParts.length, bParts.length);
            
            for (let i = 0; i < maxLength; i++) {
                const aPart = aParts[i] || 0;
                const bPart = bParts[i] || 0;
                if (aPart !== bPart) {
                    return bPart - aPart; // Descending order
                }
            }
            return 0;
        });
    }, [buildsForVersionFilter]);

    // Clear invalid filter selections when other filters change
    useEffect(() => {
        if (filterTrack) {
            const validTracks = buildsForTrackFilter.map(b => b.track).filter(Boolean);
            if (!validTracks.includes(filterTrack)) {
                setFilterTrack('');
            }
        }
    }, [buildsForTrackFilter, filterTrack]);

    useEffect(() => {
        if (filterPlatform) {
            const validPlatforms = buildsForPlatformFilter.map(b => b.platform).filter(Boolean);
            if (!validPlatforms.includes(filterPlatform)) {
                setFilterPlatform('');
            }
        }
    }, [buildsForPlatformFilter, filterPlatform]);

    useEffect(() => {
        if (filterVersion) {
            const validVersions = buildsForVersionFilter.map(b => b.version_number).filter(Boolean);
            if (!validVersions.includes(filterVersion)) {
                setFilterVersion('');
            }
        }
    }, [buildsForVersionFilter, filterVersion]);

    // Filter builds based on selected filters
    const filteredBuilds = useMemo(() => {
        return builds.filter(build => {
            if (filterTrack && build.track !== filterTrack) return false;
            if (filterPlatform && build.platform !== filterPlatform) return false;
            return !(filterVersion && build.version_number !== filterVersion);

        });
    }, [builds, filterTrack, filterPlatform, filterVersion]);

    const hasActiveFilters = filterTrack || filterPlatform || filterVersion;

    const clearFilters = () => {
        setFilterTrack('');
        setFilterPlatform('');
        setFilterVersion('');
    };

    return (
        <Box sx={{ width: fullWidth ? '100%' : 'auto' }}>
            {/* Filter Toggle Button */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <IconButton
                    size="small"
                    onClick={() => setShowFilters(!showFilters)}
                    sx={{ 
                        color: showFilters ? 'primary.main' : 'text.secondary',
                        '&:hover': { backgroundColor: 'action.hover' }
                    }}
                >
                    <FilterListIcon fontSize="small" />
                </IconButton>
                {hasActiveFilters && (
                    <Typography variant="caption" color="text.secondary">
                        {filteredBuilds.length} of {builds.length} builds
                    </Typography>
                )}
            </Box>

            {/* Filter Controls */}
            <Collapse in={showFilters}>
                <Box sx={{ 
                    mb: 2, 
                    p: 2, 
                    backgroundColor: 'action.hover', 
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider'
                }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Track</InputLabel>
                                <Select
                                    value={filterTrack}
                                    label="Track"
                                    onChange={(e) => setFilterTrack(e.target.value)}
                                >
                                    <MenuItem value="">
                                        <em>All Tracks</em>
                                    </MenuItem>
                                    {uniqueTracks.map((track) => {
                                        const trackLabel = BuildTrackChoices.find(t => t.value === track)?.label || track;
                                        return (
                                            <MenuItem key={track} value={track}>
                                                {trackLabel}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Platform</InputLabel>
                                <Select
                                    value={filterPlatform}
                                    label="Platform"
                                    onChange={(e) => setFilterPlatform(e.target.value)}
                                >
                                    <MenuItem value="">
                                        <em>All Platforms</em>
                                    </MenuItem>
                                    {uniquePlatforms.map((platform) => {
                                        const platformLabel = BuildPlatformChoices.find(p => p.value === platform)?.label || platform;
                                        return (
                                            <MenuItem key={platform} value={platform}>
                                                {platformLabel}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Version</InputLabel>
                                <Select
                                    value={filterVersion}
                                    label="Version"
                                    onChange={(e) => setFilterVersion(e.target.value)}
                                >
                                    <MenuItem value="">
                                        <em>All Versions</em>
                                    </MenuItem>
                                    {uniqueVersions.map((version) => (
                                        <MenuItem key={version} value={version}>
                                            v{version}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 1 }}>
                            {hasActiveFilters && (
                                <IconButton
                                    size="small"
                                    onClick={clearFilters}
                                    sx={{ color: 'text.secondary' }}
                                    title="Clear all filters"
                                >
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            )}
                        </Grid>
                    </Grid>
                </Box>
            </Collapse>

            {/* Build Selector */}
            <FormControl 
                fullWidth={fullWidth} 
                required={required}
                error={error}
                disabled={disabled}
            >
                <InputLabel 
                    id="build-selector-label"
                    error={error}
                >
                    {label}
                </InputLabel>
                <Select
                    labelId="build-selector-label"
                    id="build-selector"
                    value={value}
                    label={label}
                    onChange={(e) => onChange(e.target.value)}
                    error={error}
                >
                    {filteredBuilds.length === 0 ? (
                        <MenuItem value="" disabled>
                            <Typography variant="body2" color="text.secondary">
                                {builds.length === 0 ? 'No builds available' : 'No builds match filters'}
                            </Typography>
                        </MenuItem>
                    ) : (
                        filteredBuilds.map((build) => (
                            <MenuItem key={build.id} value={build.id}>
                                <Box sx={{ width: '100%' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {formatBuildLabel(build)}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                                        {build.deployment_type && (
                                            <Typography variant="caption" color="text.secondary">
                                                {build.deployment_type.replace('_', ' ')}
                                            </Typography>
                                        )}
                                        {build.released_at && (
                                            <Typography variant="caption" color="text.secondary">
                                                • Released: {formatDate(build.released_at)}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            </MenuItem>
                        ))
                    )}
                </Select>
                {helperText && (
                    <Typography 
                        variant="caption" 
                        color={error ? 'error' : 'text.secondary'}
                        sx={{ mt: 0.5, ml: 1.5 }}
                    >
                        {helperText}
                    </Typography>
                )}
            </FormControl>
        </Box>
    );
}
